import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { GuildProfile } from '@/types/guild'

interface AuthContextType {
  user: User | null
  session: Session | null
  guildProfile: GuildProfile | null
  /** True until the initial session check completes */
  initializing: boolean
  /** True once the guild profile fetch has settled for the current user */
  profileLoaded: boolean
  refreshProfile: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [guildProfile, setGuildProfile] = useState<GuildProfile | null>(null)
  const [initializing, setInitializing] = useState(true)
  const [profileLoaded, setProfileLoaded] = useState(false)

  const fetchGuildProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('guild_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (error) {
        console.warn('Guild profile fetch error:', error.message)
        setGuildProfile(null)
        return
      }
      setGuildProfile(data)
    } catch (error) {
      console.warn('Guild profile fetch failed:', error)
      setGuildProfile(null)
    } finally {
      setProfileLoaded(true)
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const initializeAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (!mounted) return
        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          await fetchGuildProfile(session.user.id)
        } else {
          setProfileLoaded(true)
        }
      } finally {
        if (mounted) setInitializing(false)
      }
    }

    initializeAuth()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event: AuthChangeEvent, session: Session | null) => {
        if (!mounted) return
        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          fetchGuildProfile(session.user.id)
        } else {
          setGuildProfile(null)
          setProfileLoaded(true)
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [fetchGuildProfile])

  const refreshProfile = useCallback(async () => {
    if (user?.id) await fetchGuildProfile(user.id)
  }, [user?.id, fetchGuildProfile])

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        guildProfile,
        initializing,
        profileLoaded,
        refreshProfile,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
